import axios from "axios";
import { API_URL } from "../components/config";
import Swal from "sweetalert2";

class AutoAssignmentService {
  // Get assignment settings
  static async getAssignmentSettings() {
    try {
      const response = await axios.get(`${API_URL}/assignment-settings`);
      return response.data || {
        auto_assignment_enabled: false,
        auto_assign_chats: true,
        auto_assign_tickets: true,
        default_algorithm: 'round_robin',
        sound_notifications: true,
        browser_notifications: true,
        max_tickets_per_agent: 5,
        max_chats_per_agent: 3
      };
    } catch (error) {
      console.error('Error fetching assignment settings:', error);
      return {
        auto_assignment_enabled: false,
        auto_assign_chats: true,
        auto_assign_tickets: true,
        default_algorithm: 'round_robin',
        sound_notifications: true,
        browser_notifications: true,
        max_tickets_per_agent: 5,
        max_chats_per_agent: 3
      };
    }
  }

  // Get available support agents
  static async getAvailableAgents(category = null) {
    try {
      const response = await axios.get(
        `${API_URL}/users/support/available${category ? `?category=${category}` : ''}`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching available agents:', error);
      return [];
    }
  }

  // Update agent support status
  static async updateAgentSupportStatus(agentId, support_status) {
    try {
      await axios.put(`${API_URL}/users/${agentId}/support-status`, { support_status });
      return true;
    } catch (error) {
      console.error('Error updating agent support status:', error);
      return false;
    }
  }

  // Auto-assign ticket
  static async autoAssignTicket(ticketId, category = null) {
    try {
      // Get current assignment settings
      const settings = await this.getAssignmentSettings();
      
      if (!settings.auto_assignment_enabled || !settings.auto_assign_tickets) {
        return { success: false, reason: 'Auto-assignment disabled for tickets' };
      }
      
      // Get available agents
      const availableAgents = await this.getAvailableAgents(category);
      
      if (availableAgents.length === 0) {
        return { success: false, reason: 'No available agents' };
      }
      
      // Check agent capacity
      const agentsUnderCapacity = availableAgents.filter(
        agent => agent.current_ticket_count < settings.max_tickets_per_agent
      );
      
      if (agentsUnderCapacity.length === 0) {
        return { success: false, reason: 'All agents at capacity' };
      }
      
      // Apply algorithm
      let selectedAgent;
      switch (settings.default_algorithm) {
        case 'round_robin':
          selectedAgent = agentsUnderCapacity[0];
          break;
        case 'fifo':
          selectedAgent = agentsUnderCapacity[0];
          break;
        case 'least_busy':
          selectedAgent = agentsUnderCapacity.sort((a, b) => a.current_ticket_count - b.current_ticket_count)[0];
          break;
        default:
          selectedAgent = agentsUnderCapacity[0];
      }
      
      // Assign ticket via API
      const response = await axios.post(
        `${API_URL}/assignment-settings/tickets/${ticketId}/auto-assign`,
        { 
          algorithm: settings.default_algorithm,
          category: category || 'general'
        }
      );
      
      if (response.data.success) {
        // Send notification to assigned agent
        await this.notifyAgent(selectedAgent.userid, {
          type: 'ticket',
          id: ticketId,
          category: category || 'general'
        });
        
        return {
          success: true,
          assigned_to: selectedAgent.userid,
          algorithm: settings.default_algorithm,
          agent_name: selectedAgent.username,
          settings_used: settings
        };
      } else {
        return { success: false, reason: response.data.message || 'Assignment failed' };
      }
      
    } catch (error) {
      console.error('Error auto-assigning ticket:', error);
      return { success: false, reason: 'Assignment failed' };
    }
  }

  // Auto-assign chat
  static async autoAssignChat(chatId, category = null) {
    try {
      // Get current assignment settings
      const settings = await this.getAssignmentSettings();
      
      if (!settings.auto_assignment_enabled || !settings.auto_assign_chats) {
        return { success: false, reason: 'Auto-assignment disabled for chats' };
      }
      
      // Get available agents
      const availableAgents = await this.getAvailableAgents(category);
      
      if (availableAgents.length === 0) {
        return { success: false, reason: 'No available agents' };
      }
      
      // Check agent capacity
      const agentsUnderCapacity = availableAgents.filter(
        agent => agent.current_chat_count < settings.max_chats_per_agent
      );
      
      if (agentsUnderCapacity.length === 0) {
        return { success: false, reason: 'All agents at capacity' };
      }
      
      // Apply algorithm
      let selectedAgent;
      switch (settings.default_algorithm) {
        case 'round_robin':
          selectedAgent = agentsUnderCapacity[0];
          break;
        case 'fifo':
          selectedAgent = agentsUnderCapacity[0];
          break;
        case 'least_busy':
          selectedAgent = agentsUnderCapacity.sort((a, b) => a.current_chat_count - b.current_chat_count)[0];
          break;
        default:
          selectedAgent = agentsUnderCapacity[0];
      }
      
      // Assign chat via API
      const response = await axios.post(
        `${API_URL}/assignment-settings/chats/${chatId}/auto-assign`,
        { 
          algorithm: settings.default_algorithm,
          category: category || 'general'
        }
      );
      
      if (response.data.success) {
        // Send notification to assigned agent
        await this.notifyAgent(selectedAgent.userid, {
          type: 'chat',
          id: chatId,
          category: category || 'general'
        });
        
        return {
          success: true,
          assigned_to: selectedAgent.userid,
          algorithm: settings.default_algorithm,
          agent_name: selectedAgent.username,
          chatId: chatId,
          settings_used: settings
        };
      } else {
        return { success: false, reason: response.data.message || 'Chat assignment failed' };
      }
      
    } catch (error) {
      console.error('Error auto-assigning chat:', error);
      return { success: false, reason: 'Chat assignment failed' };
    }
  }

  // Notify agent
  static async notifyAgent(agentId, assignment) {
    try {
      const settings = await this.getAssignmentSettings();
      
      if (settings.browser_notifications && 'Notification' in window) {
        // Request permission if not granted
        if (Notification.permission !== 'granted') {
          await Notification.requestPermission();
        }
        
        if (Notification.permission === 'granted') {
          const notification = new Notification('🎫 New Assignment', {
            body: `${assignment.type === 'ticket' ? 'Ticket' : 'Chat'} #${assignment.id} assigned to you`,
            icon: '/ticket-icon.png',
            tag: assignment.id,
            requireInteraction: true
          });
          
          notification.onclick = () => {
            const url = assignment.type === 'ticket' 
              ? `/TicketManagement/${assignment.id}`
              : `/customer-service/${assignment.id}`;
            window.open(url, '_blank');
            notification.close();
          };
        }
      }
      
      if (settings.sound_notifications) {
        // Play notification sound
        const audio = new Audio('/notification-sound.mp3');
        audio.play().catch(e => console.log('Audio play failed:', e));
      }
      
    } catch (error) {
      console.error('Error notifying agent:', error);
    }
  }

  // Update assignment settings
  static async updateAssignmentSettings(settings) {
    try {
      await axios.put(`${API_URL}/assignment-settings`, settings);
      return true;
    } catch (error) {
      console.error('Error updating assignment settings:', error);
      return false;
    }
  }

  // Show success toast (following existing pattern)
  static showSuccessToast(title, message) {
    Swal.fire({
      toast: true,
      position: "top-end",
      icon: "success",
      title: title,
      text: message,
      showConfirmButton: false,
      timer: 3000,
      timerProgressBar: true,
      background: "#002966",
      color: "#ffffff",
    });
  }

  // Show warning toast (following existing pattern)
  static showWarningToast(title, message) {
    Swal.fire({
      toast: true,
      position: "top-end",
      icon: "warning",
      title: title,
      text: message,
      showConfirmButton: false,
      timer: 4000,
      timerProgressBar: true,
      background: "#ffc000",
      color: "#002966",
    });
  }

  // Show error toast (following existing pattern)
  static showErrorToast(title, message) {
    Swal.fire({
      toast: true,
      position: "top-end",
      icon: "error",
      title: title,
      text: message,
      showConfirmButton: false,
      timer: 4000,
      timerProgressBar: true,
      background: "#dc3545",
      color: "#ffffff",
    });
  }
}

export default AutoAssignmentService;
